// Tiny settings panel — volume slider + mute toggle. Slides in like the
// leaderboard when the gear button is tapped. Audio prefs persist via
// localStorage inside src/audio.ts.

import {
  getVolume,
  isMuted,
  onMuteChange,
  onVolumeChange,
  setMuted,
  setVolume,
  sfx,
} from "../audio";

export interface SettingsHandle {
  dispose(): void;
}

export function mountSettings(
  panel: HTMLElement,
  btn: HTMLElement,
  closeBtn: HTMLElement,
): SettingsHandle {
  const slider = panel.querySelector<HTMLInputElement>("#settings-volume")!;
  const sliderOut = panel.querySelector<HTMLElement>("#settings-volume-val")!;
  const muteToggle = panel.querySelector<HTMLInputElement>("#settings-mute")!;

  function paint(): void {
    const v = Math.round(getVolume() * 100);
    slider.value = String(v);
    sliderOut.textContent = `${v}%`;
    muteToggle.checked = isMuted();
  }
  paint();

  const open = () => {
    paint();
    panel.hidden = false;
    requestAnimationFrame(() => panel.classList.add("open"));
    sfx.uiClick();
  };
  const close = () => {
    panel.classList.remove("open");
    setTimeout(() => (panel.hidden = true), 280);
  };

  const onBtn = () => (panel.classList.contains("open") ? close() : open());
  const onClose = () => close();
  const onSlider = () => {
    const v = Number(slider.value) / 100;
    setVolume(v);
    sliderOut.textContent = `${Math.round(v * 100)}%`;
  };
  const onMuteToggle = () => {
    setMuted(muteToggle.checked);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape" && panel.classList.contains("open")) close();
  };

  btn.addEventListener("click", onBtn);
  closeBtn.addEventListener("click", onClose);
  slider.addEventListener("input", onSlider);
  muteToggle.addEventListener("change", onMuteToggle);
  window.addEventListener("keydown", onKey);

  // Keep UI in sync if mute/volume are changed elsewhere (e.g., HUD mute btn).
  const unsubMute = onMuteChange(() => paint());
  const unsubVol = onVolumeChange(() => paint());

  return {
    dispose() {
      btn.removeEventListener("click", onBtn);
      closeBtn.removeEventListener("click", onClose);
      slider.removeEventListener("input", onSlider);
      muteToggle.removeEventListener("change", onMuteToggle);
      window.removeEventListener("keydown", onKey);
      unsubMute();
      unsubVol();
    },
  };
}
